import React, { useState } from 'react';
import {
  Box, Typography, Button, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel,
  CircularProgress, Alert,
} from '@mui/material';
import InlineCardForm from './InlineCardForm';

/**
 * Sentinel for the "use a new card" option.
 *
 * Lives with the chooser that renders it rather than with any one caller: both
 * the plan-change dialog and the credit-pack dialog compare against it, and two
 * copies of a magic string is one typo away from a card option that silently
 * never matches.
 */
export const NEW_CARD = '__new_card__';


/**
 * Saved cards, plus inline entry for a new one. (BILL-002)
 *
 * Rendered inside the confirm dialog rather than on its own screen: the whole
 * point of the ticket is that the user never leaves the upgrade.
 */
export const PaymentMethodChooser = ({
  methods, loading, selectedPmId, onSelect,
  inlineAvailable, clientSecret, cardRef, onCardValidityChange, error,
  savedNotice = null,
}) => {
  // Collapsed to the chosen card by default. The dialog used to name the card
  // twice — once as prose ("Charged to VISA •••• 4242.") and again as a checked
  // radio a few lines below — which reads as two different statements about the
  // same fact. One statement, with a way to change it.
  const [choosing, setChoosing] = useState(false);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2">Checking your saved cards…</Typography>
      </Box>
    );
  }

  const showingNewCard = selectedPmId === NEW_CARD;

  const selected = methods.find((m) => m.id === selectedPmId) || null;
  // Collapsed only when there is a saved card to collapse TO and the customer
  // has not asked to change it.
  const collapsed = !choosing && !showingNewCard && Boolean(selected);

  if (collapsed) {
    return (
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Payment method</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2">
            {(selected.brand || 'card').toUpperCase()} •••• {selected.last4}
          </Typography>
          <Button size="small" onClick={() => setChoosing(true)} sx={{ textTransform: 'none' }}>
            Change
          </Button>
        </Box>
        {savedNotice && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {savedNotice}
          </Typography>
        )}
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </Box>
    );
  }

  return (
    <Box>
      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend" sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
          Payment method
        </FormLabel>

        {methods.length > 0 && (
          <RadioGroup
            value={selectedPmId ?? ''}
            onChange={(e) => onSelect(e.target.value)}
            aria-label="Payment method"
          >
            {methods.map((m) => (
              <FormControlLabel
                key={m.id}
                value={m.id}
                control={<Radio size="small" />}
                label={
                  <span>
                    {(m.brand || 'card').toUpperCase()} •••• {m.last4}
                    {' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      (expires {m.exp_month}/{m.exp_year}{m.is_default ? ' · default' : ''})
                    </Typography>
                  </span>
                }
              />
            ))}
            <FormControlLabel
              value={NEW_CARD}
              control={<Radio size="small" />}
              label="Use a new card"
            />
          </RadioGroup>
        )}

        {methods.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Enter your card details to continue. Your card is sent straight to our payment
            provider — it never reaches our servers.
          </Typography>
        )}
      </FormControl>

      {/* Consent, stated before the button is pressed — and in both states of
          this component, since it is a fact about the card either way. */}
      {savedNotice && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {savedNotice}
        </Typography>
      )}

      {showingNewCard && inlineAvailable && (
        <Box sx={{ mt: 1.5 }}>
          <InlineCardForm
            ref={cardRef}
            clientSecret={clientSecret}
            onValidityChange={onCardValidityChange}
          />
        </Box>
      )}

      {showingNewCard && !inlineAvailable && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          You'll be taken to our payment provider to enter your card, and brought straight
          back here to finish.
        </Alert>
      )}

      {/* role="alert" so a decline is announced to screen readers rather than
          silently appearing below the fold. */}
      {error && (
        <Alert severity="error" role="alert" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default PaymentMethodChooser;
